import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import { Assets_URL } from "../../Apicongfig";
import { FaCompress, FaExpand, FaPaperclip, FaTimes } from "react-icons/fa";

const MessageTypeWritter = ({
  onSendMessage,
  disabled,
  editorRef,
  isEditing,
  onCancelEdit,
  participants,
  onShowAction,
  meeting,
  onFileUpload,
  attachments,
  onRemoveAttachment,
}) => {
  const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API;
  const [t] = useTranslation("global");
  const fileInputRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const FALLBACK_IMAGE = "https://via.placeholder.com/24";

  // Use a ref to store the latest unique participants
  const participantsRef = useRef([]);

  // Update the ref with unique participants
  useEffect(() => {
    const getUniqueParticipants = (participants) => {
      if (!participants || participants.length === 0) {
        return [
          {
            id: 1,
            full_name: "John Doe",
            participant_image: FALLBACK_IMAGE,
          },
          {
            id: 2,
            full_name: "Jane Smith",
            participant_image: FALLBACK_IMAGE,
          },
        ];
      }

      return participants.filter(
        (participant, index, self) =>
          index ===
          self.findIndex((p) =>
            p.email ? p.email === participant.email : p.id === participant.id
          )
      );
    };

    participantsRef.current = getUniqueParticipants(participants);
  }, [participants]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      const content = editorRef.current.getContent();
      if (content.trim() || attachments.length > 0) {
        onSendMessage({ message: content });
        editorRef.current.setContent("");
      }
    }
  };

  const uploadToCloudinary = async (blobInfo) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("file", blobInfo.blob(), blobInfo.filename());
        formData.append("upload_preset", "chat-application");
        formData.append("cloud_name", "drrk2kqvy");

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/drrk2kqvy/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        const data = await response.json();
        resolve(data.secure_url);
      } catch (error) {
        console.error("Upload error:", error);
        reject("Image upload failed: " + error.message);
      }
    });
  };

  // Initialize editor
  const handleEditorInit = (evt, editor) => {
    editorInstanceRef.current = editor;
    if (editorRef) {
      editorRef.current = editor;
    }
  };

  // Resize editor when expanded state changes
  useEffect(() => {
    if (editorInstanceRef.current) {
      const container = editorInstanceRef.current.getContainer();
      if (container) {
        container.style.height = isExpanded ? '450px' : '200px';
        editorInstanceRef.current.fire('ResizeEditor');
      }
    }
  }, [isExpanded]);

 const handleFileChange = (e) => {
    if (e.target && e.target.files) {
      onFileUpload(e); // Pass the entire event object
    }
    e.target.value = null; // Reset the input
  };


  return (
    <>
      {isExpanded && <div className="editor-overlay"></div>}
      <div
        ref={editorContainerRef}
        className={`rich-text-editor-container mt-3 ${isExpanded ? 'expanded-editor' : ''}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6>{isEditing ? t("Edit Message") : t("New Message")}</h6>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? t("Minimize") : t("Maximize")}
          >
            {isExpanded ? <FaCompress /> : <FaExpand />}
          </button>
        </div>

      
        {/* Attachments preview */}
        {attachments?.length > 0 && (
          <div className="attachments-preview mb-3 p-2 bg-light rounded">
            <div className="d-flex flex-wrap gap-2">
              {attachments?.map((file, index) => (
                <div key={index} className="position-relative">
                  {file.type.startsWith('image/') ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="img-thumbnail"
                      style={{ height: '60px', width: 'auto' }}
                    />
                  ) : (
                    <div className="d-flex align-items-center gap-2 bg-white p-2 rounded border">
                      <i className="bi bi-file-earmark-text fs-4"></i>
                      <div>
                        <div className="small text-truncate" style={{ maxWidth: '150px' }}>
                          {file.name}
                        </div>
                        <small className="text-muted">
                          {(file.size / 1024).toFixed(2)} KB
                        </small>
                      </div>
                    </div>
                  )}
                  <button
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 translate-middle"
                    style={{ padding: '0.1rem 0.25rem' }}
                    onClick={() => onRemoveAttachment(index)}
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex align-items-start gap-2">
          {/* Attachment button
           <button
            type="button"
            className="btn btn-outline-secondary mt-2"
            onClick={() => fileInputRef.current.click()}
            disabled={disabled}
            title={t("Attach files")}
          >
            <FaPaperclip />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              multiple
            />
          </button> */}

          <div className="flex-grow-1">
            <Editor
              apiKey={TINYMCE_API_KEY}
              onInit={handleEditorInit}
              initialValue=""
              init={{
                height: isExpanded ? 450 : 200,
                menubar: false,
                plugins: [
                  "advlist autolink lists link image charmap print preview anchor",
                  "searchreplace visualblocks code fullscreen",
                  "insertdatetime media table paste code help wordcount",
                  "image",
                  "imagetools",
                  "autocomplete",
                ],
                toolbar:
                  "undo redo | formatselect | " +
                  "bold italic backcolor | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | link image |" +
                  "removeformat | help",
     setup: (editor) => {
  editor.ui.registry.addAutocompleter("mentions", {
    ch: "@",
    minChars: 0,
    columns: 1,
    highlightRegex: /./i,
    fetch: (query) => {
      const currentParticipants = participantsRef.current;
      console.log("Current participants:", currentParticipants);
      const results = currentParticipants
        .filter((participant) =>
          participant?.full_name
            ?.toLowerCase()
            .includes(query.toLowerCase())
        )
        .map((participant) => {
          let imageUrl = FALLBACK_IMAGE;
          if (participant.participant_image) {
            imageUrl = participant.participant_image.startsWith("http")
              ? participant.participant_image
              : `${Assets_URL}/${participant.participant_image.replace(/^\//, "")}`;
          }
          console.log("Image URL for", participant.full_name, ":", imageUrl);
          return {
            value: participant.id.toString(),
            text: participant.full_name || "Unknown User",
            custom: `
              <div style="display: flex; align-items: center; gap: 8px; padding: 4px;">
                <img src="${imageUrl}" alt="${participant.full_name || "User"}" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;" />
                <span style="flex-grow: 1;">${participant.full_name || "Unknown User"}</span>
              </div>
            `,
            // Store image URL for onAction
            image: imageUrl,
          };
        });
      console.log("Autocomplete results:", results);
      return Promise.resolve(results);
    },
    onAction: (autocompleteApi, rng, value) => {
      const currentParticipants = participantsRef.current;
      const participant = currentParticipants.find(
        (p) => p.id.toString() === value
      );
      console.log("Selected participant:", participant);
      const fullName = participant?.full_name || "Unknown User";
      let imageUrl = participant?.participant_image
        ? participant.participant_image.startsWith("http")
          ? participant.participant_image
          : `${Assets_URL}/${participant.participant_image.replace(/^\//, "")}`
        : FALLBACK_IMAGE;
      
      editor.selection.setRng(rng);
      editor.insertContent(
        `<span class="mention" contenteditable="false" data-user-id="${value}">
          <img src="${imageUrl}" alt="${fullName}" style="width: 24px; height: 24px; border-radius: 50%; vertical-align: middle; margin-right: 4px;" 
               onerror="this.src='${FALLBACK_IMAGE}';" />
          @${fullName}
        </span> `
      );
      autocompleteApi.hide();
    },
  });
},
                automatic_uploads: true,
                images_upload_handler: async (blobInfo, progress) => {
                  progress(0);
                  try {
                    const url = await uploadToCloudinary(blobInfo);
                    progress(100);
                    return url;
                  } catch (error) {
                    progress(0);
                    throw error;
                  }
                },
                paste_data_images: true,
                file_picker_types: "image",
                image_caption: true,
                image_advtab: true,
                image_title: true,
                resize_img_proportional: true,
                object_resizing: true,
                image_dimensions: true,
                image_class_list: [
                  { title: "Responsive", value: "img-responsive" },
                  { title: "Rounded", value: "img-rounded" },
                ],
              content_style: `
  body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }
  .mention {
    background-color: #e6f3ff;
    color: #1e90ff;
    padding: 2px 4px;
    border-radius: 3px;
    cursor: pointer;
  }
  .mention:hover {
    background-color: #cce6ff;
  }
  .tox-autocompleter .tox-collection__item {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    padding: 4px !important;
  }
  .tox-autocompleter .tox-collection__item img {
    width: 24px !important;
    height: 24px !important;
    border-radius: 50% !important;
    object-fit: cover !important;
    flex-shrink: 0 !important;
  }
  .tox-autocompleter .tox-collection__item span {
    flex-grow: 1 !important;
  }
`,
              }}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-2">
           {/* Attachment button */}
           <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => fileInputRef.current.click()}
            disabled={disabled}
            title={t("Attach files")}
          >
            <FaPaperclip />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              multiple
            />
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => editorRef.current && editorRef.current.setContent("")}
            disabled={disabled}
          >
            {t("Clear")}
          </button>
          {isEditing && (
            <button
              className="btn btn-outline-danger"
              onClick={onCancelEdit}
              disabled={disabled}
            >
              {t("Cancel")}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            // disabled={disabled || (!editorRef.current?.getContent()?.trim())}
          >
            {isEditing ? t("Update Message") : t("Send Message")}
          </button>
        </div>
      </div>
    </>
  );
};

export default MessageTypeWritter;